import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Link } from 'react-router-dom';
import validator from 'validator';

import { USER_ROLE } from '@/config/constants';
import { useSignUpMutation } from '@/apis/extended/authApi';
import { navigateByRole } from './authnavigation';

type SignUpModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

function passwordStrength(
  password: string
): { label: string; color: 'error' | 'warning' | 'success' } | null {
  if (!password) return null;
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  if (score <= 2) return { label: 'Weak password', color: 'error' };
  if (score <= 3)
    return { label: 'Medium strength password', color: 'warning' };
  return { label: 'Strong password', color: 'success' };
}

export const SignUpModal = ({ isOpen, onClose }: SignUpModalProps) => {
  const theme = useTheme();
  const [signUp] = useSignUpMutation();
  const [isVerifyRequired, setIsVerifyRequired] = useState(false);
  const [error, setError] = useState('');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSignUp = () => {
    setError('');
    if (!validator.isLength(name.trim(), { min: 1 })) {
      setError('Please enter your name.');
      return;
    }
    if (!validator.isEmail(email)) {
      setError('Please input valid email address.');
      return;
    }
    if (!validator.isLength(password, { min: 8, max: 20 })) {
      setError('Password must be at least 8 characters.');
      return;
    }
    signUp({ email, password, name: name.trim() })
      .unwrap()
      .then((res) => {
        if (res.verify) {
          sessionStorage.setItem('pendingOnboarding', '1');
          setIsVerifyRequired(true);
          return;
        }
        localStorage.setItem('role', USER_ROLE.BUYER);
        navigateByRole(USER_ROLE.BUYER, res.user?._id, {
          onboardingCompleted: res.user?.onboardingCompleted ?? false,
        });
      })
      .catch((err: { data?: { message?: string }; status?: number }) => {
        const msg =
          typeof err?.data === 'string'
            ? err.data
            : err?.data?.message ?? 'Sign up failed. Please try again.';
        setError(msg);
      });
  };

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogContent
        sx={{ py: '60px', px: '32px', width: '450px', maxWidth: '100%' }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h2">Welcome to</Typography>
          <Typography
            variant="h2"
            sx={{ color: theme.palette.mainColors.blue }}
          >
            Unbeatable loans
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: theme.palette.greyShades.grey1 }}
          >
            Get started by creating an account. It&apos;s free.
          </Typography>
        </Box>
        <Box sx={{ mt: 5 }}>
          <TextField
            label="Full name"
            fullWidth
            size="small"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError('');
            }}
          />
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Email"
              type="email"
              required
              fullWidth
              size="small"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
              }}
              helperText={error}
              error={!!error}
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Password"
              required
              fullWidth
              size="small"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {password ? (
              <Typography
                variant="caption"
                color={`${passwordStrength(password)?.color}.main`}
                sx={{ display: 'block', mt: 0.5 }}
              >
                {passwordStrength(password)?.label} (min. 8 characters)
              </Typography>
            ) : null}
          </Box>

          {isVerifyRequired ? (
            <Typography
              variant="body1"
              sx={{
                mt: 3,
                textAlign: 'center',
                color: theme.palette.error.main,
              }}
            >
              Your account is secured.
              <br />
              Please check your email to complete Sign Up.
            </Typography>
          ) : (
            <Button
              variant="contained"
              fullWidth
              sx={{ mt: 3 }}
              onClick={handleSignUp}
            >
              Sign Up
            </Button>
          )}
        </Box>

        {!isVerifyRequired && (
          <Box
            sx={{ mt: 1, textAlign: 'center', maxWidth: '300px', mx: 'auto' }}
          >
            <Typography variant="caption">
              By continuing, you agree to our{' '}
              <Link to="/toc">Terms of services</Link> and{' '}
              <Link to="/privacy-policy">Privacy Policy</Link>.
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
